import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Pricing = () => {
  const { t, i18n } = useTranslation();
  const plans = t("pricing.plans", { returnObjects: true });
  const features = t("pricing.features", { returnObjects: true });
  const [loadingPlan, setLoadingPlan] = useState(null);

  const startCheckout = async (planId) => {
    if (loadingPlan) return;
    setLoadingPlan(planId);
    try {
      let userId = null;
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        userId = me.data.user_id;
      } catch (e) {
        // not signed in — checkout continues anonymously
      }
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: `wadoo_${planId}`,
        origin_url: window.location.origin,
        user_id: userId,
        language: i18n.language,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setLoadingPlan(null);
      toast.error(t("payment.checkoutError"));
    }
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 lg:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-2xl"
        >
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-mint mb-4">{t("pricing.kicker")}</p>
          <h2 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl text-balance">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-base md:text-lg text-white/55">{t("pricing.sub")}</p>
        </motion.div>

        <div className="mt-16 flex flex-wrap justify-center gap-5">
          {plans.map((plan, i) => {
            const card = (
              <div
                className={`h-full rounded-2xl p-7 flex flex-col ${
                  plan.featured ? "bg-card" : "border border-white/8 bg-white/[0.03] backdrop-blur-md"
                } transition-transform duration-300 hover:-translate-y-1.5`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-white text-lg">{plan.name}</h3>
                  {plan.save && (
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${plan.featured ? "bg-mint text-night" : "bg-mint/15 text-mint"}`}>
                      {plan.save}
                    </span>
                  )}
                </div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-semibold text-white tracking-tight">{plan.price} €</span>
                  <span className="text-sm text-white/45">{t("pricing.perMonth")}</span>
                </div>
                <p className="mt-2 text-xs text-white/40">{plan.billed}</p>
                <button
                  onClick={() => startCheckout(plan.id)}
                  disabled={loadingPlan !== null}
                  data-testid={`pricing-cta-${plan.id}`}
                  className={`mt-7 inline-flex justify-center items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform duration-200 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100 ${
                    plan.featured ? "bg-mint text-night" : "border border-white/15 text-white hover:border-mint/50"
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                  ) : (
                    t("pricing.cta")
                  )}
                </button>
              </div>
            );

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                data-testid={`pricing-card-${plan.id}`}
                className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)] xl:w-[240px]"
              >
                {plan.featured ? (
                  <div className="relative h-full">
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-mint px-3 py-1 text-[11px] font-semibold text-night whitespace-nowrap">
                      {t("pricing.bestBadge")}
                    </span>
                    <div className="beam-border h-full rounded-2xl p-[1.5px]">{card}</div>
                  </div>
                ) : (
                  card
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-14 flex flex-col items-center gap-5"
        >
          <ul className="flex flex-wrap justify-center gap-x-8 gap-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-sm text-white/60">
                <Check className="w-4 h-4 text-mint" strokeWidth={2} />
                {f}
              </li>
            ))}
          </ul>
          <p data-testid="pricing-note" className="text-xs text-white/40 tracking-wide">{t("pricing.note")}</p>
        </motion.div>
      </div>
    </section>
  );
};
